import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Settings, Save, AlertCircle } from 'lucide-react';

interface InvoiceSettings {
  id: string;
  company_name: string;
  company_address: string;
  company_phone: string | null;
  company_email: string | null;
  tax_rate: number;
  currency: string;
  invoice_prefix: string;
  notes: string | null;
}

export function InvoiceSettingsManager() {
  const [settings, setSettings] = useState<InvoiceSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('invoice_settings')
        .select('*')
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data) {
        setSettings(data);
      } else {
        const defaultSettings: Partial<InvoiceSettings> = {
          company_name: 'CoHousekeeper Maintenance',
          company_address: '123 Main Street, City, State 12345',
          company_phone: null,
          company_email: null,
          tax_rate: 0,
          currency: 'USD',
          invoice_prefix: 'INV',
          notes: null,
        };

        const { data: newData, error: insertError } = await supabase
          .from('invoice_settings')
          .insert(defaultSettings)
          .select()
          .single();

        if (insertError) throw insertError;
        setSettings(newData);
      }
    } catch (err) {
      console.error('Error loading invoice settings:', err);
      setError('Failed to load invoice settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const { error: updateError } = await supabase
        .from('invoice_settings')
        .update({
          company_name: settings.company_name,
          company_address: settings.company_address,
          company_phone: settings.company_phone,
          company_email: settings.company_email,
          tax_rate: settings.tax_rate,
          currency: settings.currency,
          invoice_prefix: settings.invoice_prefix,
          notes: settings.notes,
        })
        .eq('id', settings.id);

      if (updateError) throw updateError;

      setSuccessMessage('Invoice settings saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving invoice settings:', err);
      setError('Failed to save invoice settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = <K extends keyof InvoiceSettings>(
    field: K,
    value: InvoiceSettings[K]
  ) => {
    if (settings) {
      setSettings({ ...settings, [field]: value });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <p>Unable to load invoice settings</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-secondary-600" />
          <h2 className="text-xl font-semibold text-gray-900">Invoice Settings</h2>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          Customize how your invoices appear and calculate totals
        </p>
      </div>

      <form onSubmit={handleSave} className="p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-800">
              <Save className="w-5 h-5" />
              <p>{successMessage}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name *
            </label>
            <input
              type="text"
              required
              value={settings.company_name}
              onChange={(e) => updateField('company_name', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              placeholder="Your Company Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invoice Prefix *
            </label>
            <input
              type="text"
              required
              value={settings.invoice_prefix}
              onChange={(e) => updateField('invoice_prefix', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              placeholder="INV"
            />
            <p className="mt-1 text-xs text-gray-500">
              Appears before invoice number (e.g., INV-2024-001)
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Address *
          </label>
          <textarea
            required
            value={settings.company_address}
            onChange={(e) => updateField('company_address', e.target.value)}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
            placeholder="123 Main Street&#10;City, State 12345"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Phone
            </label>
            <input
              type="tel"
              value={settings.company_phone || ''}
              onChange={(e) => updateField('company_phone', e.target.value || null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Email
            </label>
            <input
              type="email"
              value={settings.company_email || ''}
              onChange={(e) => updateField('company_email', e.target.value || null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              placeholder="billing@company.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tax Rate (%)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={settings.tax_rate}
              onChange={(e) => updateField('tax_rate', parseFloat(e.target.value) || 0)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              placeholder="8.5"
            />
            <p className="mt-1 text-xs text-gray-500">
              Applied to all invoices (e.g., 8.5 for 8.5% tax)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            <select
              value={settings.currency}
              onChange={(e) => updateField('currency', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CAD">CAD ($)</option>
              <option value="AUD">AUD ($)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Default Invoice Notes
          </label>
          <textarea
            value={settings.notes || ''}
            onChange={(e) => updateField('notes', e.target.value || null)}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
            placeholder="Payment terms, thank you message, or other notes to include on all invoices..."
          />
          <p className="mt-1 text-xs text-gray-500">
            These notes will appear at the bottom of all invoices
          </p>
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
